import { Message } from 'dg-web-shared/lib/Localized.ts';
import {
    Navigate,
    Outlet,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { RecodeConditionType } from 'dg-web-shared/model/RecodeCondition.ts';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { BarrierGateIdentificationEntity } from '../offstreet-transactions/OperatorOffstreetTransactionTypes.ts';
import { useOffstreetTransactionEntryWithoutExitOutlet } from '../offstreet-transactions/OperatorOffstreetTransactionEntryWithoutExit.tsx';

export type EntryWithoutExitRecodeCondition =
    | FreeEntryWithoutExitRecodeCondition
    | TariffEntryWithoutExitRecodeCondition
    | AmountEntryWithoutExitRecodeCondition
    | TimeEntryWithoutExitRecodeCondition;

export interface EntryWithoutExitRecodeConditionBase {
    entryWithoutExitId: number;
    recodeConditionId: string;
    recodeConditionDescription: Message;
    zoneDescription: string;
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    entryDateTime: string;
    type: RecodeConditionType;
}

export interface FreeEntryWithoutExitRecodeCondition extends EntryWithoutExitRecodeConditionBase {
    type: RecodeConditionType.FREE;
}

export interface TariffEntryWithoutExitRecodeCondition extends EntryWithoutExitRecodeConditionBase {
    tariffName: string;
    type: RecodeConditionType.TARIFF;
}

export interface AmountEntryWithoutExitRecodeCondition extends EntryWithoutExitRecodeConditionBase {
    freeAmount: number;
    type: RecodeConditionType.AMOUNT;
}

export interface TimeEntryWithoutExitRecodeCondition extends EntryWithoutExitRecodeConditionBase {
    freeTimeMin: number;
    type: RecodeConditionType.TIME;
}

export enum RecodeErrorType {
    PARSING_FAILED = 'PARSING_FAILED',
    IDENTIFICATION_UNKNOWN = 'IDENTIFICATION_UNKNOWN',
    ZONE_NOT_AVAILABLE = 'ZONE_NOT_AVAILABLE',
    ALREADY_PAID = 'ALREADY_PAID',
    ONE_TIME_EXIT_SCANNED = 'ONE_TIME_EXIT_SCANNED',
    ALREADY_RECODED = 'ALREADY_RECODED',
    NOT_REGULAR_CONTEXT = 'NOT_REGULAR_CONTEXT',
    NO_TRANSACTION_FOUND = 'NO_TRANSACTION_FOUND',
    MULTIPLE_TRANSACTION_FOUND = 'MULTIPLE_TRANSACTION_FOUND',
}

export function CloudConnectorRecodeTicketScanConditionsOutlet() {
    const { ticketScan } = useParams<{ ticketScan: string }>();
    const [applicableRecodeConditionListState] = useServerFetch<
        EntryWithoutExitRecodeCondition[],
        {
            ticketScan: string;
        },
        RecodeErrorType
    >(
        ({ ticketScan }) => ({
            url: `/ui-api/operator-account/cloud-connector/recode/ticketScan/${ticketScan}`,
        }),
        ticketScan ? { ticketScan: ticketScan } : null,
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.MOBILE}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={applicableRecodeConditionListState}
                    render={recodeConditions => (
                        <RecodeEntryWithoutExitOutlet
                            context={{
                                recodeConditions: recodeConditions,
                                offstreetTransactionSearchQuery: '',
                            }}
                        />
                    )}
                    renderError={recodeError => (
                        <Navigate to={`../error/${recodeError}`} />
                    )}
                />
            )}
        />
    );
}

export function CloudConnectorRecodeTicketScanQrConditionsOutlet() {
    const { ticketId } = useParams<{ ticketId: string }>();
    const [applicableRecodeConditionListState] = useServerFetch<
        EntryWithoutExitRecodeCondition[],
        {
            ticketId: string;
        },
        RecodeErrorType
    >(
        ({ ticketId }) => ({
            url: `/ui-api/operator-account/cloud-connector/recode/ticketScanQr/${ticketId}`,
        }),
        ticketId ? { ticketId: ticketId } : null,
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.MOBILE}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={applicableRecodeConditionListState}
                    render={recodeConditions => (
                        <RecodeEntryWithoutExitOutlet
                            context={{
                                recodeConditions: recodeConditions,
                                offstreetTransactionSearchQuery: '',
                            }}
                        />
                    )}
                    renderError={recodeError => (
                        <Navigate to={`../error/${recodeError}`} />
                    )}
                />
            )}
        />
    );
}

export function CloudConnectorRecodeLicensePlateConditions() {
    const { licensePlateNr } = useParams<{ licensePlateNr: string }>();
    const [fetchLicensePlateRecodeConditionState] = useServerFetch<
        EntryWithoutExitRecodeCondition[],
        {
            licensePlateNr: string;
        },
        RecodeErrorType
    >(
        ({ licensePlateNr }) => ({
            url: `/ui-api/operator-account/cloud-connector/recode/license-plate/${licensePlateNr}`,
        }),
        licensePlateNr ? { licensePlateNr: licensePlateNr } : null,
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.MOBILE}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={fetchLicensePlateRecodeConditionState}
                    render={recodeConditions => (
                        <RecodeEntryWithoutExitOutlet
                            context={{
                                recodeConditions: recodeConditions,
                                offstreetTransactionSearchQuery: '',
                            }}
                        />
                    )}
                    renderError={recodeError => (
                        <Navigate to={`../../error/${recodeError}`} />
                    )}
                />
            )}
        />
    );
}

export function CloudConnectorRecodeEntryWithoutExitByIdConditionsOutlet() {
    const urlParams = useParams<{ entryWithoutExitId: string }>();
    const entryWithoutExitId = urlParams.entryWithoutExitId
        ? urlParams.entryWithoutExitId
        : null;
    const { refetchEntryWithoutExitDetailDesktop } =
        useOffstreetTransactionEntryWithoutExitOutlet();
    const query = useQuery();
    const [applicableRecodeConditionListState] = useServerFetch<
        EntryWithoutExitRecodeCondition[],
        {
            entryWithoutExitId: string;
        },
        RecodeErrorType
    >(
        ({ entryWithoutExitId }) => ({
            url: `/ui-api/operator-account/cloud-connector/recode/entry-without-exit/${entryWithoutExitId}`,
        }),
        entryWithoutExitId ? { entryWithoutExitId: entryWithoutExitId } : null,
    );
    return (
        <OperatorRoutedModal
            variant={ModalVariant.SMALL}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={applicableRecodeConditionListState}
                    render={recodeConditions => (
                        <RecodeEntryWithoutExitOutlet
                            context={{
                                recodeConditions: recodeConditions,
                                refetchEntryWitoutExitDetailDesktop:
                                    refetchEntryWithoutExitDetailDesktop,
                                offstreetTransactionSearchQuery: `?${query}`,
                            }}
                        />
                    )}
                    renderError={recodeError => (
                        <Navigate to={`error/${recodeError}`} />
                    )}
                />
            )}
        />
    );
}

export function useRecodeEntryWithoutExitOutlet() {
    return useOutletContext<RecodeEntryWithoutExitOutletContext>();
}

function RecodeEntryWithoutExitOutlet({
    context,
}: {
    context: RecodeEntryWithoutExitOutletContext;
}) {
    return <Outlet context={context} />;
}

interface RecodeEntryWithoutExitOutletContext {
    recodeConditions: EntryWithoutExitRecodeCondition[];
    refetchEntryWitoutExitDetailDesktop?: () => void;
    offstreetTransactionSearchQuery: string;
}
